import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import {
  listAssets, createAsset, checkinAsset, setAssetStatus,
  ASSET_TYPES, ASSET_TYPE_LABELS, ASSET_STATUS_LABELS,
  type Asset, type AssetType, type AssetStatus, type Utilization,
} from "../lib/assets";

const STATUS_STYLE: Record<AssetStatus, string> = {
  available: "text-verified", in_use: "text-accent-strong", maintenance: "text-[#c9a227]", lost: "text-danger",
};

export function Assets() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [util, setUtil] = useState<Utilization | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ tag: "", type: "trailer" as AssetType, label: "" });

  async function refresh() {
    const r = await listAssets();
    setAssets(r.assets); setUtil(r.utilization);
  }
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!form.tag) return;
    setBusy(true);
    try {
      await createAsset({ tag: form.tag, type: form.type, label: form.label || undefined });
      setForm({ tag: "", type: "trailer", label: "" });
      await refresh();
    } finally { setBusy(false); }
  }

  async function toggle(a: Asset) {
    setBusy(true);
    try {
      await checkinAsset({ id: a.id, action: a.status === "in_use" ? "check_in" : "check_out", by_name: a.status === "in_use" ? undefined : "Yard dispatch" });
      await refresh();
    } finally { setBusy(false); }
  }

  async function flagLost(a: Asset) {
    setBusy(true);
    try { await setAssetStatus(a.id, a.status === "lost" ? "available" : "lost"); await refresh(); } finally { setBusy(false); }
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Assets & trailers"
        subtitle="Track trailers, containers, chassis, and equipment — check them out and in with location, no hardware required."
        actions={util && (
          <span className="font-mono text-[11px] text-muted" data-testid="utilization">
            {util.in_use}/{util.total} in use · {Math.round(util.utilization_rate * 100)}% utilized
          </span>
        )}
      />

      <Panel title="Register an asset">
        <form className="grid gap-3 sm:grid-cols-4" onSubmit={add}>
          <input className={inputClass} placeholder="Tag (e.g. TRL-4471)" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} aria-label="Asset tag" />
          <select className={inputClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as AssetType })} aria-label="Asset type">
            {ASSET_TYPES.map((t) => <option key={t} value={t}>{ASSET_TYPE_LABELS[t]}</option>)}
          </select>
          <input className={inputClass} placeholder="Label (optional)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <Button type="submit" disabled={busy} data-testid="add-asset">Add asset</Button>
        </form>
      </Panel>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : assets.length === 0 ? (
        <p className="font-mono text-sm text-muted">No assets yet. Register a trailer or container above.</p>
      ) : (
        <Panel title={`Assets (${assets.length})`}>
          <ul className="grid gap-2" data-testid="asset-list">
            {assets.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 border-b border-line pb-2 text-sm last:border-0 last:pb-0">
                <span className="grid gap-0.5">
                  <span className="font-mono font-bold">{a.tag}</span>
                  <span className="font-mono text-[11px] text-muted">
                    {ASSET_TYPE_LABELS[a.type]}{a.label ? ` · ${a.label}` : ""}{a.held_by ? ` · held by ${a.held_by}` : ""}
                    {a.last_seen_at ? ` · seen ${new Date(a.last_seen_at).toLocaleDateString()}` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <span className={`font-mono text-[11px] font-bold uppercase ${STATUS_STYLE[a.status]}`}>{ASSET_STATUS_LABELS[a.status]}</span>
                  <button type="button" className="font-mono text-[11px] text-accent-strong hover:underline" disabled={busy} onClick={() => toggle(a)}>
                    {a.status === "in_use" ? "Check in" : "Check out"}
                  </button>
                  <button type="button" className="font-mono text-[11px] text-danger hover:underline" disabled={busy} onClick={() => flagLost(a)}>
                    {a.status === "lost" ? "Found" : "Lost"}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
